import React, { useState, useEffect, useRef, useContext } from "react";
import "./samoprocenaforma.css";
import { baseRequest } from "../../../axiosconfig";
import Loading from "../../loading";
import { Link, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { PRINT_PAGE_STYLE } from "../../../utils/printPageStyle";
import { UserContext } from "../../../userContext";
import { UnsavedDataContext } from "../../../context";
import { ROLES } from "../../../constants/roles";

const ANSWER_OPTIONS = [
  { value: "da", label: "Da" },
  { value: "ne", label: "Ne" },
  { value: "delimicno", label: "Delimično" },
];

function SamoprocenaForma() {
  const params = useParams();
  const { user } = useContext(UserContext);
  const canEditRole = user && (user.role_id === ROLES.SUPER_ADMIN || user.role_id === ROLES.MANAGER);

  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [sections, setSections] = useState([]);
  const [openSection, setOpenSection] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState("");
  const componentRef = useRef();
  const { setUnsavedData } = useContext(UnsavedDataContext);

  const readOnly = !canEditRole || report?.status === "zavrsen";

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: PRINT_PAGE_STYLE,
  });

  // Nesačuvani odgovori aktiviraju standardno upozorenje pri navigaciji
  useEffect(() => {
    setUnsavedData(dirty);
    return () => setUnsavedData(false);
  }, [dirty, setUnsavedData]);

  const loadReport = () => {
    setLoading(true);
    baseRequest
      .get(`/self_assessment_report/${params.id}`)
      .then((res) => {
        setReport(res.data.data);
        setSections(res.data.data.sections || []);
        // otvorenu sekciju ne diramo pri ponovnom učitavanju (npr. posle
        // snimanja) — podrazumevano se otvara samo prva, samo prvi put
        setOpenSection((current) =>
          current || (res.data.data.sections?.length ? res.data.data.sections[0].code : null)
        );
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    loadReport();
  }, [params.id]);

  const setAnswer = (sectionCode, questionId, field, value) => {
    setDirty(true);
    setMessage("");
    setSections((prev) =>
      prev.map((s) => {
        if (s.code !== sectionCode) return s;
        return {
          ...s,
          questions: s.questions.map((q) =>
            q.id === questionId ? { ...q, [field]: value } : q
          ),
        };
      })
    );
  };

  const handleSave = () => {
    const answers = sections.flatMap((s) =>
      s.questions.map((q) => ({
        question_id: q.id,
        answer: q.answer,
        comment: q.comment,
      }))
    );
    setLoading(true);
    baseRequest
      .put(`/self_assessment_report/${report.id}/answers`, { answers })
      .then(() => {
        setDirty(false);
        setMessage("Odgovori su sačuvani.");
        loadReport();
      })
      .catch((err) => {
        setLoading(false);
        setMessage(err.response?.data?.message || "Čuvanje nije uspelo.");
      });
  };

  const handleFinalize = () => {
    if (!window.confirm("Da li ste sigurni da želite da završite upitnik? Nakon završetka izmene više neće biti moguće.")) return;
    setLoading(true);
    baseRequest
      .put(`/self_assessment_report/${report.id}/finalize`)
      .then(() => loadReport())
      .catch((err) => {
        setLoading(false);
        setMessage(err.response?.data?.message || "Završavanje nije uspelo.");
      });
  };

  const answeredCount = sections.reduce(
    (sum, s) => sum + s.questions.filter((q) => q.answer).length,
    0
  );
  const totalCount = sections.reduce((sum, s) => sum + s.questions.length, 0);

  const renderQuestions = (forPrint) =>
    sections.map((section) => (
      <div key={section.code} className="samoprocena-forma-section">
        <div
          className="samoprocena-forma-section-header"
          onClick={() =>
            !forPrint &&
            setOpenSection(openSection === section.code ? null : section.code)
          }
        >
          <span>
            {section.order}. {section.name}
          </span>
          <span>
            {section.questions.filter((q) => q.answer).length}/
            {section.questions.length}
          </span>
        </div>
        {(forPrint || openSection === section.code) && (
          <ul className="samoprocena-forma-questions">
            {section.questions.map((q) => (
              <li key={q.id} className="samoprocena-forma-question">
                <p className="samoprocena-forma-question-text">
                  <strong>{q.question_number}</strong> {q.text}
                </p>
                <div className="samoprocena-forma-answers">
                  {ANSWER_OPTIONS.map((opt) => (
                    <label key={opt.value}>
                      <input
                        type="radio"
                        name={`q-${q.id}${forPrint ? "-print" : ""}`}
                        checked={q.answer === opt.value}
                        disabled={readOnly || forPrint}
                        onChange={() =>
                          setAnswer(section.code, q.id, "answer", opt.value)
                        }
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
                {forPrint ? (
                  q.comment && (
                    <p className="samoprocena-forma-comment-print">
                      Komentar: {q.comment}
                    </p>
                  )
                ) : (
                  <textarea
                    className="samoprocena-forma-comment"
                    placeholder="Komentar..."
                    value={q.comment || ""}
                    disabled={readOnly}
                    onChange={(e) =>
                      setAnswer(section.code, q.id, "comment", e.target.value)
                    }
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    ));

  if (!report) {
    return (
      <div style={{ position: "relative", minHeight: "200px" }}>
        <Loading isLoading={loading} />
      </div>
    );
  }

  return (
    <div className="samoprocena-forma">
      <div className="processes-header" style={{ padding: "15px 0", justifyContent: "space-between" }}>
        <div className="back-arrow">
          <img src="/images/backarrow.png" alt="" />
          <Link to="/admin/samoprocena">Nazad</Link>
        </div>
        <div style={{ display: "flex", gap: "10px", marginRight: "10px" }}>
          <button className="add-btn-large" onClick={handlePrint}>
            <img alt="" src="/images/printer.png"></img>
          </button>
        </div>
      </div>

      <h2 className="samoprocena-forma-title">
        UPITNIK ZA SAMOPROCENU SISTEMA FUK — {report.year}. GODINA
      </h2>
      <p className="samoprocena-forma-status">
        Status: {report.status === "zavrsen" ? "Završen" : "U izradi"} |
        Popunjeno: {answeredCount}/{totalCount}
      </p>

      {renderQuestions(false)}

      {!readOnly && (
        <div className="samoprocena-forma-actions">
          <button className="add-poslovni-proces-btn" onClick={handleSave}>
            <img src="/images/check2.png" alt="" />
            <span>Sačuvaj odgovore</span>
          </button>
          <button
            className="add-poslovni-proces-btn"
            onClick={handleFinalize}
            disabled={dirty}
            title={dirty ? "Prvo sačuvajte odgovore" : ""}
          >
            <span>Završi upitnik</span>
          </button>
        </div>
      )}
      {message && <p className="samoprocena-forma-message">{message}</p>}

      <div style={{ display: "none" }}>
        <div ref={componentRef} className="samoprocena-forma-print">
          <h2>UPITNIK ZA SAMOPROCENU SISTEMA FUK — {report.year}. GODINA</h2>
          <p>
            Status: {report.status === "zavrsen" ? "Završen" : "U izradi"} |
            Datum štampe: {new Date().toLocaleDateString("sr-RS")}
          </p>
          {renderQuestions(true)}
        </div>
      </div>

      <Loading isLoading={loading} />
    </div>
  );
}

export default SamoprocenaForma;
